var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { getSession } from '../session';
import {} from '../authz';
import { requirementFor } from '../entitlements';
/*
 * <vitesse-gated> — the UI half of a policy annotation.
 *
 * The server declares what guards an RPC on the RPC itself. This is the same
 * declaration on the view that calls it:
 *
 *   <vitesse-gated .rpc=${Controllers.method.subscribeLogs}>
 *     <controller-logs-section></controller-logs-section>
 *   </vitesse-gated>
 *
 * It gates against the app's session (`initSession` at boot), so a view wraps and
 * is done — there is no session to thread through the component tree, and none to
 * forget.
 *
 * WHY A COMPONENT rather than a helper each view calls. `Session` already answers
 * every question — allows(), why(), atLeast(). What it cannot do is make every
 * view answer them the SAME way, and a lock rendered slightly differently in four
 * places is how a product ends up with four upgrade prompts. Wrapping is a
 * decision a component author makes once; rendering a lock is a decision they
 * would otherwise make every time.
 *
 * It renders the LOCK, never the data. The content is a slot, so a gated section
 * is the same element it would have been ungated — nothing about a view's
 * structure changes when it becomes gated.
 *
 * LOAD-BEARING: this is UX. The server refuses the call regardless, and anyone
 * with dev tools can delete this element. It exists so a person is told why
 * something is unavailable instead of clicking it and getting an error.
 */
let VitesseGated = class VitesseGated extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Render a compact inline chip instead of a panel.
         *
         * For a control in a toolbar or an action row: a bordered panel where a button
         * used to be is worse than the problem it solves, but hiding the control means a
         * customer never discovers the feature exists to be bought. The chip keeps it
         * discoverable at the size of the thing it replaced.
         */
        this.inline = false;
        /**
         * Render nothing at all instead of a lock.
         *
         * For a control inside a dense row — a button in a toolbar — where a lock panel
         * would be worse than absence. Used sparingly: a person who cannot see that a
         * feature exists cannot know it is buyable, so the default tells them.
         */
        this.hide = false;
        this.tick = 0;
    }
    static { this.styles = css `
    :host {
      display: contents;
    }
    .locked {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--space-2, 0.5rem);
      padding: var(--space-6, 1.5rem);
      border: 1px dashed var(--border-color);
      border-radius: var(--radius-md, 8px);
      background: var(--surface-raised);
      color: var(--text-secondary);
      text-align: center;
    }
    .reason {
      font-size: var(--font-size-sm, 0.875rem);
      max-width: 42ch;
      line-height: 1.45;
    }
    .icon {
      font-size: 1.25rem;
      line-height: 1;
    }
    /* Inline: for a control in a dense row, where a panel would be absurd but
       hiding it would mean a customer never learns the feature is buyable. */
    .locked.inline {
      display: inline-flex;
      flex-direction: row;
      gap: var(--space-1, 0.25rem);
      padding: var(--space-1, 0.25rem) var(--space-2, 0.5rem);
      font-size: var(--font-size-sm, 0.875rem);
      white-space: nowrap;
    }
    .locked.inline .icon {
      font-size: 0.9rem;
    }
    .locked.inline .reason {
      max-width: none;
    }
  `; }
    /** The session actually in force: the one passed, else the app's. */
    get #session() {
        return this.session ?? getSession();
    }
    #off;
    connectedCallback() {
        super.connectedCallback();
        // Subscribe on connect, drop on disconnect — the symmetry is the point of
        // doing this in a component rather than in each view's own lifecycle.
        this.#off = this.#session?.onChange(() => (this.tick += 1));
    }
    disconnectedCallback() {
        this.#off?.();
        this.#off = undefined;
        super.disconnectedCallback();
    }
    updated(changed) {
        if (changed.has('session')) {
            this.#off?.();
            this.#off = this.#session?.onChange(() => (this.tick += 1));
        }
    }
    render() {
        // No session at all — not passed, and the app never booted one. Render the
        // content. Fail-OPEN, matching Session: a missing session is a wiring bug, and
        // hiding a paying customer's features because of one is the worse failure.
        const session = this.#session;
        if (!session)
            return html `<slot></slot>`;
        // The rpc's own annotation wins when given: one declaration, read by both
        // sides. The explicit attributes remain for views that compose several calls
        // and so have no single method to point at.
        // One decision, from Session. The element used to compose permission,
        // capability and level itself and render its own sentence for a level refusal
        // — a second implementation of a decision the grid and every other vanilla
        // caller also needs, and a second place a refusal could be worded differently.
        // An rpc WAS given but carries no requirement: refuse, exactly as
        // Session.allowsRpc does and as the server now does.
        //
        // This used to fall through to the explicit attributes — all undefined for an
        // rpc-driven gate — which composed an empty requirement and rendered OPEN. So
        // `allowsRpc(X)` said no while `<vitesse-gated .rpc=${X}>` said yes for the
        // same X: two answers to one question, from the same module, and the open one
        // is the answer a reviewer never sees because the control simply works.
        //
        // The attribute path stays for views that compose several calls and so have no
        // single method to point at; it is only reachable when no rpc was given.
        const fromRpc = this.rpc ? requirementFor(this.rpc) : undefined;
        if (this.rpc && !fromRpc) {
            if (this.hide)
                return nothing;
            return html `
        <div class="locked ${this.inline ? 'inline' : ''}" role="note"
             title="this action declares no authorization requirement">
          <span class="icon" aria-hidden="true">🔒</span>
        </div>
      `;
        }
        const base = fromRpc ?? {
            permission: this.permission,
            capability: this.capability,
            level: this.level,
        };
        const req = this.consumes ? { ...base, consumes: this.consumes } : base;
        const usage = this.consumes && this.used !== undefined ? { [this.consumes]: this.used } : undefined;
        if (session.allows(req, usage))
            return html `<slot></slot>`;
        if (this.hide)
            return nothing;
        const why = session.why(req, usage) ?? 'this is unavailable on your plan';
        return html `
      <div class="locked ${this.inline ? 'inline' : ''}" role="note" title=${why}>
        <span class="icon" aria-hidden="true">🔒</span>
        <span class="reason">${why}</span>
      </div>
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseGated.prototype, "session", void 0);
__decorate([
    property({ attribute: false })
], VitesseGated.prototype, "rpc", void 0);
__decorate([
    property({ type: String })
], VitesseGated.prototype, "permission", void 0);
__decorate([
    property({ type: String })
], VitesseGated.prototype, "capability", void 0);
__decorate([
    property({ type: String })
], VitesseGated.prototype, "level", void 0);
__decorate([
    property({ type: String })
], VitesseGated.prototype, "consumes", void 0);
__decorate([
    property({ attribute: false })
], VitesseGated.prototype, "used", void 0);
__decorate([
    property({ type: Boolean })
], VitesseGated.prototype, "inline", void 0);
__decorate([
    property({ type: Boolean })
], VitesseGated.prototype, "hide", void 0);
__decorate([
    state()
], VitesseGated.prototype, "tick", void 0);
VitesseGated = __decorate([
    customElement('vitesse-gated')
], VitesseGated);
export { VitesseGated };
//# sourceMappingURL=vitesse-gated.js.map